import * as React from "react";
import {
  LineChart as RLineChart,
  Line,
  BarChart as RBarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

type SeriesDef = {
  key: string;
  label: string;
  color?: string;
};

type Props = {
  data: Array<Record<string, any>>;
  xKey: string;
  title?: string;
  xLabel?: string;
  yLabel?: string;
  xName?: string;
  series: SeriesDef[];
  height?: number;
  chartType?: "line" | "bar";
  xFormatter?: (v: number) => string;
  yFormatter?: (v: number) => string;
  valueFormatter?: (v: number, key: string) => string;
  darkMode?: boolean;
};

const DEFAULT_COLORS = ["#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6"];

function roundTo1(v: number): string {
  if (!Number.isFinite(v)) return String(v);
  return Number.isInteger(v) ? String(v) : v.toFixed(1);
}

export default function MultiSeriesLineChart({
  data,
  xKey,
  title,
  xLabel,
  yLabel,
  xName,
  chartType = "line",
  series,
  height = 280,
  xFormatter = (v) => roundTo1(Number(v)),
  yFormatter = (v) => roundTo1(Number(v)),
  valueFormatter = (v) => roundTo1(Number(v)),
  darkMode = false,
}: Props) {
  // Legend toggle
  const [hidden, setHidden] = React.useState<Record<string, boolean>>({});
  const handleLegendClick = (o: any) => {
    const key = o?.dataKey as string;
    if (!key) return;
    setHidden((curr) => ({ ...curr, [key]: !curr[key] }));
  };

  const textColor  = "#111827";
  const gridColor  = darkMode ? "#374151" : "#E5E7EB";
  const axisStroke = "#111827";

  // ── X values ─────────────────────────────────────────────────────────────
  const xValues = React.useMemo(() => {
    const arr = (data ?? [])
      .map((d) => Number((d as any)[xKey]))
      .filter((v) => Number.isFinite(v));
    return [...new Set(arr)].sort((a, b) => a - b);
  }, [data, xKey]);

  const minX = xValues[0] ?? 0;
  const maxX = xValues[xValues.length - 1] ?? 0;

  const [range, setRange] = React.useState<[number, number]>([minX, maxX]);
  React.useEffect(() => { setRange([minX, maxX]); }, [minX, maxX]);

  const filteredData = React.useMemo(() => {
    const [min, max] = range;
    return (data ?? []).filter((d) => {
      const x = Number((d as any)[xKey]);
      if (!Number.isFinite(x)) return true;
      return x >= min && x <= max;
    });
  }, [data, range, xKey]);

  const xTicks = React.useMemo(() => {
    if (xValues.length < 2) return xValues;
    const min = xValues[0];
    const max = xValues[xValues.length - 1];
    const count = 8;
    return Array.from({ length: count + 1 }, (_, i) =>
      min + (i * (max - min)) / count
    );
  }, [xValues]);

  // ── X ticks for bar chart (evenly spaced from actual categories) ─────────
  const barXTicks = React.useMemo(() => {
    const n = filteredData.length;
    if (n <= 1) return filteredData.map((d) => d[xKey]);
    const count = Math.min(8, n - 1);
    return Array.from({ length: count + 1 }, (_, i) => {
      const idx = Math.round((i * (n - 1)) / count);
      return filteredData[idx][xKey];
    });
  }, [filteredData, xKey]);

  // ── Peak index per series (for bar highlighting) ──────────────────────────
  const peakIndexPerSeries = React.useMemo(() => {
    const result: Record<string, number> = {};
    for (const s of series) {
      let best = -1;
      let bestVal = -Infinity;
      filteredData.forEach((d, i) => {
        const v = Number(d[s.key]);
        if (Number.isFinite(v) && Math.abs(v) > bestVal) {
          bestVal = Math.abs(v);
          best = i;
        }
      });
      result[s.key] = best;
    }
    return result;
  }, [filteredData, series]);

  return (
    <div style={{ width: "100%", color: textColor }}>
      {title && <h3 style={{ marginBottom: 8, color: textColor }}>{title}</h3>}
      <ResponsiveContainer width="100%" height={height}>
        {chartType === "bar" ? (
          <RBarChart data={filteredData} margin={{ top: 10, right: 10, bottom: 20, left: 10 }}>
            <CartesianGrid
              horizontal={true}
              vertical={false}
              stroke="#D1D5DB"
              strokeOpacity={0.8}
            />
            <XAxis
              dataKey={xKey}
              type="category"
              ticks={barXTicks}
              tickFormatter={(v) => String(Math.round(Number(v)))}
              tick={{ fill: "#111827", fontSize: 12 }}
              axisLine={{ stroke: "#111827" }}
              tickLine={false}
              label={xLabel ? { value: xLabel, position: "insideBottom", dy: 14, fill: "#111827", fontSize: 12, textAnchor: "middle" } : undefined}
            />
            <YAxis
              tickFormatter={(v) => yFormatter(Number(v))}
              tick={{ fill: "#111827", fontSize: 12 }}
              axisLine={{ stroke: "#111827" }}
              tickLine={false}
              label={yLabel ? { value: yLabel, angle: -90, position: "insideLeft", fill: "#111827", fontSize: 11, style: { textAnchor: "middle" } } : undefined}
            />
            <Tooltip
              contentStyle={{ backgroundColor: "#FFFFFF", border: "1px solid #E5E7EB", color: "#111827", borderRadius: 10 }}
              labelFormatter={(label) => { const name = (xLabel ?? "x").replace(/\s*\[.*?\]/, "").trim(); return `${xName ?? name}: ${xFormatter(Number(label))}`; }}
              formatter={(value: any, name: string) => {
                const num = Number(value);
                const label = series.find((s) => s.key === name)?.label ?? name;
                return [valueFormatter(Number.isFinite(num) ? num : 0, name), label];
              }}
            />
            <Legend verticalAlign="top" wrapperStyle={{ cursor: "pointer", paddingBottom: 8 }} onClick={handleLegendClick} />
            {series.map((s, i) => {
              const baseColor = s.color ?? DEFAULT_COLORS[i % DEFAULT_COLORS.length];
              const peakIdx   = peakIndexPerSeries[s.key] ?? -1;
              return (
                <Bar
                  key={s.key}
                  dataKey={s.key}
                  name={s.label}
                  fill={baseColor}  
                  hide={!!hidden[s.key]}
                  radius={[6, 6, 0, 0]}
                >
                  {filteredData.map((_, idx) => (
                    <Cell
                      key={`cell-${s.key}-${idx}`}
                      fill={baseColor}
                      opacity={idx === peakIdx ? 1 : 0.4}
                    />
                  ))}
                </Bar>
              );
            })}
          </RBarChart>
        ) : (
          <RLineChart
            data={filteredData}
            margin={{ top: 10, right: 10, bottom: 20, left: 10 }}
          >
            <CartesianGrid strokeDasharray="" stroke="#D1D5DB" strokeOpacity={0.8} />
            <XAxis
              dataKey={xKey}
              type="number"
              domain={['dataMin', 'dataMax']}
              ticks={xTicks}
              tickFormatter={(v) => xFormatter(Number(v))}
              tick={{ fill: axisStroke, fontSize: 12, dy: 0 }}
              axisLine={{ stroke: gridColor }}
              tickLine={false}
              label={
                xLabel
                  ? { value: xLabel, position: "insideBottom", dy: 14, fill: axisStroke, fontSize: 12, textAnchor: "middle" }
                  : undefined
              }
            />
            <YAxis
              tickFormatter={(v) => yFormatter(Number(v))}
              tick={{ fill: axisStroke, fontSize: 12, dx: 0, dy: 0 }}
              axisLine={{ stroke: gridColor }}
              tickLine={false}
              label={
                yLabel
                  ? { value: yLabel, angle: -90, position: "insideLeft", fill: axisStroke, fontSize: 12, textAnchor: "middle" }
                  : undefined
              }
            />
            <Tooltip
              contentStyle={{
                backgroundColor: "#ffffff",
                border: `1px solid ${darkMode ? "#374151" : "#E5E7EB"}`,
                color: textColor,
                borderRadius: 10,
                boxShadow: "0 2px 8px rgba(0,0,0,0.10)",
              }}
              labelStyle={{ color: textColor }}
              formatter={(value: any, name: string) => {
                const num = Number(value);
                const label = series.find((s) => s.key === name)?.label ?? name;
                return [valueFormatter(Number.isFinite(num) ? num : 0, name), label];
              }}
              labelFormatter={(label) => `${xName ?? xLabel ?? "x"}: ${xFormatter(Number(label))}`}
            />
            <Legend
              verticalAlign="top"
              wrapperStyle={{ cursor: "pointer", paddingBottom: 8 }}
              onClick={handleLegendClick}
            />
            {series.map((s, i) => (
              <Line
                key={s.key}
                type="monotone"
                dataKey={s.key}
                name={s.label}
                stroke={s.color ?? DEFAULT_COLORS[i % DEFAULT_COLORS.length]}
                dot={false}
                strokeWidth={2}
                hide={!!hidden[s.key]}
                isAnimationActive={false}
              />
            ))}
          </RLineChart>
        )}
      </ResponsiveContainer>
    </div>
  );
}
